import { SocialMediaPostData } from "@contracts/shareStop";
import { PhotoGalleryCustomerData } from "@contracts/photosPageData";

export type ContentCalendarInputSource = "local" | "template";

export interface ContentCalendarInput {
  id: string;

  customerId: string;
  title: string;
  description: string;
  date: string;
  source: ContentCalendarInputSource;
  // Optional external URL associated with the calendar input
  url?: string | null;

  order?: number | null;

  // Audit fields
  createdDate: string;
  createdBy: string;
  updatedDate: string | null;
  updatedBy: string | null;
}

export interface GetCalendarInputsResponse {
  inputs: Array<ContentCalendarInput>;
  shouldCreateLocalEvents: boolean;
  shouldGenInterests: boolean;
}

export interface CreateContentCalendarInputRequest {
  customerId: string;
  title: string;
  description: string;
  date: string;
  source: ContentCalendarInputSource;
}

export interface GenerateContentCalendarRequest {
  userGuidance?: string;
  genStartDate?: string;
  matchContent?: boolean;
  saveResults?: boolean;
  deleteAndRegen?: boolean; // Delete the current set and regenerates
}

export interface ContentMatchRequest {
  overwriteIfSet?: boolean; // If true, will overwrite the current contentMatch if it already exists
}

export enum ContentSummaryStatus {
  New = "New",
  Done = "Done",
  Failed = "Failed",
  Redo = "Redo",
}

export interface ContentSummaryData {
  id: string;

  customerId: string;
  contextType: ContentSummaryContextType;
  contextId: string;
  contextName: string;
  aiSummary: string;

  photoCount: number;
  videoCount: number;
  assetCount: number;

  // Example asset IDs from the content group
  examples: Array<string>;

  status: ContentSummaryStatus;

  lastProcessed: string;

  // Audit fields
  createdOn: string;
  createdBy: string;
  updatedOn: string | null;
  updatedBy: string | null;
}

export interface ContentSummaryPayload {
  content: Array<ContentSummaryData>;
}

export interface ContentSummaryStatusResults {
  completeCount: number;
  needsProcessingCount: number;
  failedCount: number;
}

export enum ContentSummaryContextType {
  CommunityUpload = "CommunityUpload",
  CustomerUpload = "CustomerUpload",
  CreatorVisit = "CreatorVisit",
  Location = "Location",
}

export enum CalendarItemGenStatus {
  Draft = "Draft",
  Pending = "Pending",
  Generating = "Generating",
  Completed = "Completed",
  Failed = "Failed",
}

export interface GenMetaInfo {
  sourceType?: string;
  locationExternalId?: string;
  contentFit?: string;
  affinityScore?: number;
  candidateContentSummaryIds?: Array<string>;
  rationale?: string;
  foundContent?: boolean;
}

export interface ContentCalendarItem {
  entityType: "ContentCalendarItem";

  id: string;

  matchRationale: string;
  genStatus: CalendarItemGenStatus;
  genMetaInfo?: GenMetaInfo;

  // Entity-specific properties
  customerId: string;
  title: string;
  description: string | null;
  specificDate: string | null;
  weekGroup: string; // e.g. "2025-W27"
  order: number;
  contentSource: ContentSummaryContextType;
  contentSourceId: string;

  // Audit fields
  createdDate: string;
  createdBy: string;
  updatedDate: string | null;
  updatedBy: string | null;
}

export interface ContentCalendarItemMatch {
  calendarItem: ContentCalendarItem;
  contentMatch: ContentSummaryData;
  socialPost?: SocialMediaPostData;
}

export interface ContentCalendarWeekMatch {
  weekGroup: string;
  weekGroupName: string;
  postWillGenDate?: string;
  items: Array<ContentCalendarItemMatch>;
}

export interface ContentCalendarPrimaryGroup {
  weeks: Array<ContentCalendarWeekMatch>;
}

export enum CalendarPrimaryGroupName {
  Past = "past",
  Upcoming = "upcoming",
}

export interface ContentCalendarMatchedResults {
  [CalendarPrimaryGroupName.Past]: ContentCalendarPrimaryGroup;
  [CalendarPrimaryGroupName.Upcoming]: ContentCalendarPrimaryGroup;

  currentOperation: SocialOperationType | null;
  nextPostGenForWeek: string;
  nextPostGenOnWeek: string;
  topicFilterCount: number;
}

export interface MarkReadyForPostGenRequest {
  customerId: string;

  weekKey?: string; // weekKey from where to start (the number of weeks to generate posts for is defined server side)

  // Specific calendar item IDs to generate for.
  // NOTE: Mutually exclusive with WeekKey.
  calendarItemIds?: Array<string>;
}

export interface StatusChangeCalendarItemInput {
  id: string;
  genStatus: CalendarItemGenStatus;
}

export interface StatusChangeCheckRequest {
  inputs: Array<StatusChangeCalendarItemInput>;
}

export interface SocialContentUploadPayload {
  uploadedForPostTitle: string;
  photoIds: Array<string>;
  clientSideDateTime: string; // DateTimeOffset
}

export interface SocialContentUploadResult {
  contentSummaryData: ContentSummaryData;
  photoGallery: PhotoGalleryCustomerData;
}

export interface MatchAndGeneratePostRequest {
  title: string;
  description: string;
  weekGroup?: string | null;
}

export interface MatchAndGeneratePostResponse {
  calendarItem: ContentCalendarItem;
  failedToMatch: boolean;
}

export interface CalendarDashboardSummaryResult {
  postsPendingApproval: Array<SocialMediaPostData>;
  scheduledNotPublishedCount: number;
  nextPostGenForWeek: string | null;
  nextPostGenOnWeek: string | null;
  nextPostScheduledOn: string | null;
  postsPerWeek: number;
  weeksToGenPosts: number;
}

export enum SocialOperationType {
  InitialSetup = "InitialSetup",
  NeedInitialContent = "NeedInitialContent",
  NewContentGen = "NewContentGen",
  // Add other types as needed
}

export enum OperationStatus {
  Pending = "Pending",
  InProgress = "InProgress",
  Completed = "Completed",
  Failed = "Failed",
}

export interface CalendarStatusResult {
  currentOperation: SocialOperationType | null;
  status: OperationStatus | null;
}

export interface CalendarGenResultSummaryData {
  id: string;
  customerId: string;
  calendarId: string;
  startDate: string; // DateOnly as ISO string
  weeksGenerated: number;
  contentSummariesCount: number;
  totalPhotoCount: number;
  topicsGenerated: number;
  topicsMatched: number;
  topicSummary: string | null;
  moreContentNeeded: string | null;
  moreContentGuidance: string | null;
  history: string | null;
  moreContentNeededRationale: string | null;
  status: "New" | "Done" | "Failed" | "Redo";
  isProcessing: boolean;
  setId: string;
  source: "Manual" | "InitialSetup" | "WeeklyJob";
  createdDate: string;
  lastRequestedProcessingDate: string | null;
  updatedDate: string;
}

export interface NextFutureCalendarResult {
  nextCalendarGenSummary: CalendarGenResultSummaryData | null;
  longTermCalendarGenSummary: CalendarGenResultSummaryData | null;
  enableUI: boolean;
}
